import { AlertMessage } from "@/components/reusable";
import { ProviderSelector } from "@/components/providers/ProviderSelector";
import { SchemaTemplateOptionCard } from "@/components/schema-builder/SchemaTemplateOptionCard";
import { SchemaTemplateSelectionModal } from "@/components/schema-builder/SchemaTemplateSelectionModal";
import { getNextTemplateVersionName, isSchemaNameTaken } from "@/lib/schemaBuilderHelpers";
import { listSchemas } from "@/services/schemaBuilderApi";
import type { SchemaSummary, SchemaTemplate } from "@/types/schemaBuilder";
import { ArrowLeft, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function SchemaTemplateSetupPage() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<SchemaTemplate[]>([]);
  const [allSchemas, setAllSchemas] = useState<SchemaSummary[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SchemaTemplate | null>(null);
  const [schemaName, setSchemaName] = useState("");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    listSchemas(true)
      .then((schemas) => {
        setAllSchemas(schemas);
        setTemplates(
          schemas
            .filter((s) => s.status === "preset" || s.status === "published")
            .map((s) => ({
              id: s.id,
              name: s.name,
              description: s.description,
              status: s.status as "preset" | "published",
              totalColumns: s.totalColumns,
              rules: s.rules,
              crossColumnRules: 0,
              knownMappings: 0,
            }))
        );
      })
      .catch(() => {})
      .finally(() => setIsLoadingTemplates(false));
  }, []);

  const canStart = Boolean(selectedTemplate) && schemaName.trim().length > 0 && providerId !== null;

  async function handleStartBuilding() {
    if (!selectedTemplate || !canStart) return;

    setError("");
    setIsChecking(true);
    try {
      const schemas = await listSchemas(true);
      if (isSchemaNameTaken(schemaName, schemas)) {
        setError("A schema with this name already exists. Please choose a different name.");
        return;
      }
    } finally {
      setIsChecking(false);
    }

    // The edit page's useParams() picks up this ID and calls createSchemaFromTemplate.
    navigate(`/schema-builder/${selectedTemplate.id}`, {
      state: {
        source: "template",
        schemaName: schemaName.trim(),
        templateName: selectedTemplate.name,
        providerId,
      },
    });
  }

  return (
    <div className="min-h-[calc(100vh-72px)] animate-fade-in">
      <section className="px-12 py-10">
        <button
          type="button"
          onClick={() => navigate("/schema-builder/create")}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="mb-8 text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
          Build Schema from Template
        </h1>

        <div className="max-w-[520px] space-y-6">
          {!selectedTemplate ? (
            <button
              type="button"
              onClick={() => setIsTemplateModalOpen(true)}
              disabled={isLoadingTemplates}
              className="h-10 w-full rounded-md border border-[var(--color-border)] bg-white px-3 text-sm text-[var(--color-text-primary)] transition hover:border-[var(--color-action-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)]"
            >
              {isLoadingTemplates ? "Loading templates…" : "Select Template"}
            </button>
          ) : (
            <div className="relative">
              <SchemaTemplateOptionCard template={selectedTemplate} compact />

              <button
                type="button"
                onClick={() => {
                  setSelectedTemplate(null);
                  setSchemaName("");
                  setError("");
                }}
                className="absolute -right-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--color-border)] bg-white text-[var(--color-text-secondary)] shadow-sm hover:bg-[var(--color-bg-secondary)]"
                aria-label="Remove selected template"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Provider
            </label>
            <ProviderSelector value={providerId} onChange={setProviderId} />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Name Your Schema
            </label>

            <input
              value={schemaName}
              onChange={(event) => {
                setSchemaName(event.target.value);
                setError("");
              }}
              placeholder={
                selectedTemplate ? `${selectedTemplate.name} Custom v1` : "Schema Name ABC"
              }
              disabled={!selectedTemplate}
              className="h-10 w-full rounded-md border border-[var(--color-border)] px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
            />

            {error && <AlertMessage variant="error" message={error} className="mt-3" />}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleStartBuilding}
              disabled={!canStart || isChecking}
              className="rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
            >
              {isChecking ? "Checking..." : "Start Building"}
            </button>
          </div>
        </div>
      </section>

      {isTemplateModalOpen && (
        <SchemaTemplateSelectionModal
          templates={templates}
          selectedTemplate={selectedTemplate}
          onSelectTemplate={(template) => {
            setSelectedTemplate(template);
            setSchemaName(getNextTemplateVersionName(template.name, allSchemas));
          }}
          onClose={() => setIsTemplateModalOpen(false)}
          onConfirm={() => setIsTemplateModalOpen(false)}
        />
      )}
    </div>
  );
}
